import { useEffect, useState } from "react"
import { useSelector } from "react-redux"
import { getSiteContent, updateSiteContentEntry } from "../services/siteContentService"
import { RootState } from "../store/store"

// Loads the editable copy for one marketing page (pilot: Home) and
// exposes an in-place "edit mode" for admins, in the spirit of a
// WordPress front-end editor: text falls back to whatever default the
// caller passes when no entry has been saved for that key yet, so
// pages work exactly as before until someone actually edits something.
export function useSiteContent(page: string) {
  const isAdmin = useSelector((state: RootState) => Boolean(state.auth.user?.isAdmin))
  const [content, setContent] = useState<Record<string, string>>({})
  const [loading, setLoading] = useState(true)
  const [editMode, setEditMode] = useState(false)
  const [savingKey, setSavingKey] = useState<string | null>(null)

  useEffect(() => {
    let cancelled = false
    setLoading(true)

    getSiteContent(page)
      .then((result) => {
        if (!cancelled) setContent(result)
      })
      .catch(() => {
        if (!cancelled) setContent({})
      })
      .finally(() => {
        if (!cancelled) setLoading(false)
      })

    return () => {
      cancelled = true
    }
  }, [page])

  function get(key: string, fallback: string) {
    return content[key] ?? fallback
  }

  async function save(key: string, value: string) {
    setSavingKey(key)
    try {
      await updateSiteContentEntry(key, value)
      setContent((prev) => ({ ...prev, [key]: value }))
    } finally {
      setSavingKey(null)
    }
  }

  return {
    isAdmin,
    loading,
    editMode,
    setEditMode: isAdmin ? setEditMode : () => {},
    get,
    save,
    savingKey
  }
}
